import { useCallback, useEffect, useRef, useState } from 'react';
import { TEAMS } from './teams';
import { isOpen, type Ticket } from './mock';

/**
 * Escalation chains per team: while a ticket is still unacknowledged (status
 * "New"), each step reassigns it up the chain after its timer and notifies.
 * The engine fires off the live clock whenever the app is open. (Firing when
 * nobody is online needs a server cron / Edge Function — wired with integrations.)
 */
export type EscalationTarget = 'oncall' | 'lead';
export type EscalationStep = { afterMin: number; assignTo: EscalationTarget };
export type EscalationPolicy = { enabled: boolean; steps: EscalationStep[] };

export const DEFAULT_ESCALATION: EscalationPolicy = {
  enabled: false,
  steps: [
    { afterMin: 30, assignTo: 'oncall' },
    { afterMin: 120, assignTo: 'lead' },
  ],
};

const LS_KEY = 'compass-escalation';

function loadAll(): Record<string, EscalationPolicy> {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch {
    return {};
  }
}

export function getEscalationPolicy(teamId: string): EscalationPolicy {
  return loadAll()[teamId] ?? DEFAULT_ESCALATION;
}

export function useEscalationPolicy(teamId: string) {
  const [map, setMap] = useState<Record<string, EscalationPolicy>>(() => loadAll());
  const policy = map[teamId] ?? DEFAULT_ESCALATION;
  const save = useCallback(
    (next: EscalationPolicy) => {
      const all = loadAll();
      const merged = { ...all, [teamId]: next };
      localStorage.setItem(LS_KEY, JSON.stringify(merged));
      setMap(merged);
    },
    [teamId],
  );
  return { policy, save };
}

/** Drives escalation while the app is open. */
export function useEscalationEngine(
  tickets: Ticket[],
  escalate: (id: string, assignee: string, level: number, label: string) => void,
) {
  const ref = useRef(tickets);
  ref.current = tickets;

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      for (const t of ref.current) {
        if (t.state !== 'New' || !isOpen(t)) continue; // acknowledged / closed → stop
        const team = TEAMS.find((tm) => tm.name === t.team || tm.match.includes(t.team));
        if (!team) continue;
        const pol = getEscalationPolicy(team.id);
        if (!pol.enabled || pol.steps.length === 0) continue;
        const level = t.escalationLevel ?? 0;
        const step = pol.steps[level];
        if (!step) continue;
        if ((now - t.createdAt) / 60000 >= step.afterMin) {
          const target = step.assignTo === 'oncall' ? team.oncall ?? team.members[0] : team.members[0];
          escalate(t.id, target, level + 1, `Auto-escalated to ${target} (L${level + 1})`);
        }
      }
    };
    const id = setInterval(tick, 8000);
    tick();
    return () => clearInterval(id);
  }, [escalate]);
}
